import { ArrowRight12 } from '@mgonetwork/icons';
import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from '@tanstack/react-table';
import './style.scss';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { TableHeader } from '~/ui/TableHeader';
export interface TableCardProps<DataType extends object> {
	refetching?: boolean;
	data: DataType[];
	columns: ColumnDef<DataType>[];
	sortTable?: boolean;
	showBorder?: boolean;
	defaultSorting?: SortingState;
	noBorderBottom?: boolean;
	AllValidators?: boolean;
}

function AscDescIcon({ sorting }: { sorting: 'asc' | 'desc' }) {
	return (
		<ArrowRight12
			fill="currentColor"
			className={clsx(sorting === 'asc' ? '-rotate-90' : 'rotate-90', ' text-steel-darker')}
		/>
	);
}

export function TableCard<DataType extends object>({
	refetching,
	data,
	columns,
	sortTable,
	showBorder,
	defaultSorting,
	noBorderBottom,
	AllValidators,
}: TableCardProps<DataType>) {
	const [sorting, setSorting] = useState<SortingState>(defaultSorting || []);

	// Use Columns to create a table
	const processedcol = useMemo<ColumnDef<DataType>[]>(
		() =>
			columns.map((column) => ({
				...column,
				// cell renderer for each column from react-table
				// cell should be in the column definition
				//TODO: move cell to column definition
				...(!sortTable && { cell: ({ getValue }) => getValue() }),
			})),
		[columns, sortTable],
	);

	const table = useReactTable({
		data,
		columns: processedcol,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: setSorting,
		enableSorting: !!sortTable,
		enableSortingRemoval: false,
		initialState: {
			sorting,
		},
		state: {
			sorting,
		},
	});

	return (
		<div
			className={clsx(
				`w-full overflow-x-auto pb-4 ${showBorder ? '' : 'border-bc-1 rounded-[10px] p-5'} `,
				// !noBorderBottom && 'border-b border-gray-45',
				refetching && 'opacity-50',
			)}
		>
			{AllValidators && (
				<div className="defined-bg-22 mb-6 rounded-[4px] p-5">
					<TableHeader>All Validators</TableHeader>
				</div>
			)}
			<table className="w-full min-w-max border-collapse text-left">
				<thead className="defined-bg-21 text-indent-[20px] h-[56px]">
					{' '}
					{/* 修改了这里 */}
					{table.getHeaderGroups().map((headerGroup) => (
						<tr key={headerGroup.id}>
							{headerGroup.headers.map(({ id, colSpan, column, isPlaceholder, getContext }) => (
								<th
									key={id}
									colSpan={colSpan}
									scope="col"
									className="h-7.5 text-left text-subtitle font-semibold uppercase text-steel-dark"
									onClick={
										column.columnDef.enableSorting ? column.getToggleSortingHandler() : undefined
									}
								>
									<div
										className={clsx('flex items-center gap-1', 'cursor-pointer text-steel-darker')}
									>
										{isPlaceholder ? null : flexRender(column.columnDef.header, getContext())}
										{column.getIsSorted() && (
											<AscDescIcon sorting={column.getIsSorted() as 'asc' | 'desc'} />
										)}
									</div>
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody>
					{table.getRowModel().rows.map((row) => (
						<tr key={row.id} className="group h-[72px]">
							{row.getVisibleCells().map(({ column, id, getContext }) => (
								<td
									key={id}
									style={{ paddingLeft: '20px' }}
									className="h-7.5 border-b border-gray-45 text-body text-hero-dark group-hover:bg-gray-40 group-hover:text-gray-90 group-hover:first:rounded-l group-hover:last:rounded-r"
								>
									{flexRender(column.columnDef.cell, getContext())}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
